import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Plus, Search, Trash2, X } from 'lucide-react';
import Drawer from '../UI/Drawer';
import ConfirmModal from '../UI/ConfirmModal';
import { fieldLabel, fieldLabelStyle, inputCls, inputStyle, monoLabel, primaryBtn, primaryBtnStyle, secondaryBtn, secondaryBtnStyle } from '../UI/ink';
import { sevBg, sevFg } from './model';

// Placeholders are values the backend accepts (documentation ranges such as 203.0.113.0/24 are rejected).
const TYPES = [
  { value: 'ip', label: 'IP address', placeholder: '45.9.148.9' },
  { value: 'domain', label: 'Domain', placeholder: 'bad.example.com' },
  { value: 'hash', label: 'SHA-256', placeholder: '9f86d081884c7d659a2feaa0c55ad015a3bf4f1b2b0b822cd15d6c15b0f00a08' },
];
const LEVELS = ['high', 'medium', 'low'];
const LEVEL_SEV = { high: 'high', medium: 'med', low: 'low' };
const EMPTY_FORM = { ioc_type: 'ip', ioc_value: '', threat_level: 'high', description: '', attack_technique: '' };

// An organisation's private indicators: add, search and delete them without
// leaving the alerts inbox. `iocs` is the usePrivateIocs() result.
const PrivateIndicatorsDrawer = ({ open, onClose, iocs, canManage }) => {
  const { items, limit, loading, loadError, error, deleteError, saving, reload, add, remove, clearError, clearDeleteError } = iocs;
  const [form, setForm] = useState(EMPTY_FORM);
  const [query, setQuery] = useState('');
  const [pending, setPending] = useState(null);
  const [refocusTick, setRefocusTick] = useState(0);
  const searchRef = useRef(null);
  const confirmRef = useRef(null);
  const triggerRef = useRef(null);

  // A confirmation and any add/delete error belong to one opening of the drawer.
  useEffect(() => {
    if (open) return;
    setPending(null);
    clearError();
    clearDeleteError();
  }, [open, clearError, clearDeleteError]);

  // Put focus back on the trash button that opened the confirmation, or the
  // search box if that row is gone, so it never falls to <body>.
  const closeConfirm = useCallback(() => {
    setPending(null);
    const trigger = triggerRef.current;
    if (trigger && trigger.isConnected && !trigger.disabled) trigger.focus();
    else searchRef.current?.focus();
  }, []);

  // Escape cancels the confirmation first; the drawer's own Escape handler
  // skips events that were already handled.
  useEffect(() => {
    if (!open || !pending) return undefined;
    const onKey = (e) => {
      if (e.key !== 'Escape') return;
      e.preventDefault();
      closeConfirm();
    };
    document.addEventListener('keydown', onKey, true);
    return () => document.removeEventListener('keydown', onKey, true);
  }, [open, pending, closeConfirm]);

  // Focus Cancel (the safe choice) when the confirmation opens.
  useEffect(() => {
    if (!open || !pending) return;
    const buttons = confirmRef.current ? [...confirmRef.current.querySelectorAll('button')] : [];
    buttons.find((b) => b.textContent.trim() === 'Cancel')?.focus();
  }, [open, pending]);

  // After a delete the focused trash button is gone with its row.
  useEffect(() => {
    if (!refocusTick) return;
    const active = document.activeElement;
    if (!active || active === document.body || !active.isConnected) searchRef.current?.focus();
  }, [refocusTick]);

  const set = (key) => (e) => {
    if (error) clearError();
    setForm((f) => ({ ...f, [key]: e.target.value }));
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!canManage) return;
    if (await add(form)) setForm(EMPTY_FORM);
  };

  const askDelete = (item, e) => {
    triggerRef.current = e.currentTarget;
    setPending(item);
  };

  const confirmDelete = async () => {
    const item = pending;
    closeConfirm();
    if (item && await remove(item)) setRefocusTick((t) => t + 1);
  };

  const q = query.trim().toLowerCase();
  const shown = items.filter((i) =>
    !q || `${i.ioc_value} ${i.description} ${i.attack_technique}`.toLowerCase().includes(q));
  const placeholder = (TYPES.find((t) => t.value === form.ioc_type) || TYPES[0]).placeholder;

  return (
    <>
      <Drawer
        open={open}
        onClose={onClose}
        kicker="YOUR ORGANISATION"
        title="Private indicators"
        subtitle="Only your organisation can see these. A match raises an alert for your organisation only."
      >
        <div className="flex flex-col gap-7">
          <form onSubmit={submit} aria-label="Add a private indicator" className="flex flex-col gap-4">
            <div className="grid grid-cols-1 sm:grid-cols-[150px_minmax(0,1fr)] gap-4">
              <div className="flex flex-col gap-1.5">
                <label htmlFor="pi-type" className={fieldLabel} style={fieldLabelStyle}>Type</label>
                <select id="pi-type" value={form.ioc_type} onChange={set('ioc_type')} disabled={!canManage} className={inputCls} style={inputStyle}>
                  {TYPES.map((t) => <option key={t.value} value={t.value}>{t.label}</option>)}
                </select>
              </div>
              <div className="flex flex-col gap-1.5">
                <label htmlFor="pi-value" className={fieldLabel} style={fieldLabelStyle}>Value</label>
                <input id="pi-value" type="text" required value={form.ioc_value} onChange={set('ioc_value')} disabled={!canManage}
                  placeholder={placeholder} autoComplete="off" spellCheck={false} className={`${inputCls} font-mono`} style={inputStyle} />
              </div>
              <div className="flex flex-col gap-1.5">
                <label htmlFor="pi-level" className={fieldLabel} style={fieldLabelStyle}>Threat level</label>
                <select id="pi-level" value={form.threat_level} onChange={set('threat_level')} disabled={!canManage} className={inputCls} style={inputStyle}>
                  {LEVELS.map((l) => <option key={l} value={l}>{l}</option>)}
                </select>
              </div>
              <div className="flex flex-col gap-1.5">
                <label htmlFor="pi-technique" className={fieldLabel} style={fieldLabelStyle}>ATT&amp;CK technique (optional)</label>
                <input id="pi-technique" type="text" value={form.attack_technique} onChange={set('attack_technique')} disabled={!canManage}
                  placeholder="T1071" autoComplete="off" spellCheck={false} className={`${inputCls} font-mono`} style={inputStyle} />
              </div>
            </div>
            <div className="flex flex-col gap-1.5">
              <label htmlFor="pi-description" className={fieldLabel} style={fieldLabelStyle}>Description (optional)</label>
              <input id="pi-description" type="text" maxLength={500} value={form.description} onChange={set('description')} disabled={!canManage}
                className={inputCls} style={inputStyle} />
            </div>
            {error && (
              <div role="alert" className="px-3.5 py-2.5 rounded-[10px] border text-[13px]" style={{ borderColor: 'var(--sev-crit-fg)', color: 'var(--sev-crit-fg)', background: 'var(--sev-crit-bg)' }}>{error}</div>
            )}
            <div className="flex items-center gap-3">
              <button type="submit" disabled={!canManage || saving} className={primaryBtn} style={primaryBtnStyle}>
                <Plus className="w-4 h-4" strokeWidth={2} />Add indicator
              </button>
              {!canManage && (
                <span className="text-[12.5px]" style={{ color: 'var(--ink-muted)' }}>Adding or removing indicators needs an administrator.</span>
              )}
            </div>
          </form>

          <section className="flex flex-col gap-3" aria-label="Your indicators">
            {deleteError && (
              <div role="alert" className="sticky top-0 z-10 flex items-start justify-between gap-3 px-3.5 py-2.5 rounded-[10px] border text-[13px]" style={{ borderColor: 'var(--sev-crit-fg)', color: 'var(--sev-crit-fg)', background: 'var(--sev-crit-bg)' }}>
                <span>{deleteError}</span>
                <button type="button" onClick={clearDeleteError} aria-label="Dismiss" className="shrink-0"><X className="w-4 h-4" /></button>
              </div>
            )}
            <div className="flex items-center justify-between">
              <span className={monoLabel} style={{ color: 'var(--ink-faint)' }}>INDICATORS</span>
              {!loading && !loadError && (
                <span className="font-mono text-[11.5px]" style={{ color: 'var(--ink-faint)' }}>{items.length} of {limit}</span>
              )}
            </div>
            <div className="h-10 box-border flex items-center gap-2 px-3 rounded-[10px] border" style={{ background: 'var(--surf-bg)', borderColor: 'var(--surf-line-strong)' }}>
              <Search className="w-[15px] h-[15px] shrink-0" style={{ color: 'var(--ink-faint)' }} strokeWidth={1.8} />
              <input ref={searchRef} type="search" aria-label="Search indicators" placeholder="Search by value, description or technique" value={query} onChange={(e) => setQuery(e.target.value)}
                className="flex-grow min-w-0 h-[38px] bg-transparent border-0 outline-none text-[13.5px]" style={{ color: 'var(--ink-text)' }} />
            </div>

            {loading ? (
              <p className="m-0 py-6 text-center text-sm" style={{ color: 'var(--ink-faint)' }}>Loading…</p>
            ) : loadError ? (
              <div className="flex flex-wrap items-center gap-3 px-3.5 py-3 rounded-[10px] border" style={{ borderColor: 'var(--sev-crit-fg)', background: 'var(--sev-crit-bg)' }}>
                <p role="alert" className="m-0 flex-grow text-[13px]" style={{ color: 'var(--sev-crit-fg)' }}>{loadError}</p>
                <button type="button" onClick={reload} className={`${secondaryBtn} h-9`} style={secondaryBtnStyle}>Retry</button>
              </div>
            ) : items.length === 0 ? (
              <p className="m-0 py-6 text-center text-sm" style={{ color: 'var(--ink-faint)' }}>No private indicators yet. Add one above to start monitoring for it.</p>
            ) : (
              <ul aria-label="Indicator list" className="m-0 p-0 list-none flex flex-col">
                {shown.map((item) => {
                  const sev = LEVEL_SEV[item.threat_level] || 'unk';
                  return (
                    <li key={item.id} className="flex items-start gap-3 py-3 border-b last:border-b-0" style={{ borderColor: 'var(--surf-line)' }}>
                      <div className="flex-grow min-w-0 flex flex-col gap-1.5">
                        <span className="font-mono text-[13px] break-all">{item.ioc_value}</span>
                        <span className="flex flex-wrap items-center gap-2 text-xs" style={{ color: 'var(--ink-muted)' }}>
                          <span className="font-mono">{item.ioc_type}</span>
                          <span className="px-2 py-0.5 rounded-full text-[11px] font-medium" style={{ color: sevFg(sev), background: sevBg(sev) }}>{item.threat_level}</span>
                          {item.attack_technique && <span className="font-mono">{item.attack_technique}</span>}
                        </span>
                        {item.description && <span className="text-xs leading-snug" style={{ color: 'var(--ink-muted)' }}>{item.description}</span>}
                      </div>
                      {canManage && (
                        <button type="button" aria-label={`Delete ${item.ioc_value}`} onClick={(e) => askDelete(item, e)}
                          className="w-9 h-9 shrink-0 flex items-center justify-center rounded-[10px] border" style={{ borderColor: 'var(--surf-line)', color: 'var(--ink-muted)' }}>
                          <Trash2 className="w-4 h-4" strokeWidth={1.8} />
                        </button>
                      )}
                    </li>
                  );
                })}
              </ul>
            )}
          </section>
        </div>

        {/* Inside the drawer so it is part of the modal dialog, not outside it. */}
        {pending && (
          <div ref={confirmRef} role="alertdialog" aria-modal="true" aria-label={`Delete ${pending.ioc_value}?`}>
            <ConfirmModal
              isOpen
              title={`Delete ${pending.ioc_value}?`}
              message="It will stop raising alerts for your organisation."
              confirmLabel="Delete"
              danger
              onConfirm={confirmDelete}
              onCancel={closeConfirm}
            />
          </div>
        )}
      </Drawer>
    </>
  );
};

export default PrivateIndicatorsDrawer;
